import { useState } from "react"
import {
    Typography,
    Button,
    Card,
    Table,
    Input,
    message,
    Modal,
    Form,
    Tag,
} from "antd"
import {
    CheckCircleOutlined,
    CreditCardOutlined,
} from "@ant-design/icons"

const { Title, Text } = Typography

// Helper for Ant Design message
const showMessage = (type, content) => {
    message[type](content)
}

const BillingSubscription = () => {
    const [isCancelModalVisible, setIsCancelModalVisible] = useState(false)
    const [paymentMethodForm] = Form.useForm()
    const [billingContactForm] = Form.useForm()

    const billingHistoryData = [
        { key: "1", invoice: "INV-1001", date: "June 1", amount: "$199.00", status: "Paid" },
        { key: "2", invoice: "INV-0999", date: "May 1", amount: "$199.00", status: "Paid" },
        { key: "3", invoice: "INV-0998", date: "April 1", amount: "$199.00", status: "Paid" },
    ]

    const billingHistoryColumns = [
        { title: "Invoice #", dataIndex: "invoice", key: "invoice" },
        { title: "Date", dataIndex: "date", key: "date" },
        { title: "Amount", dataIndex: "amount", key: "amount" },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (text) => <Tag color={text === "Paid" ? "green" : "red"}>{text}</Tag>,
        },
        {
            title: "Action",
            key: "action",
            render: () => (
                <Button type="link" size="small">
                    Download
                </Button>
            ),
        },
    ]

    const handleUpdatePaymentMethod = (values) => {
        showMessage("success", "Payment method updated.")
        // In a real app, this would send data to payment gateway
    }

    const handleCancelSubscription = () => {
        Modal.confirm({
            title: "Confirm Cancellation",
            content: (
                <div>
                    <p>Are you sure you want to cancel your subscription?</p>
                    <p className="text-red-500 mt-2">You will retain access until the end of your current billing cycle.</p>
                </div>
            ),
            onOk: () => {
                showMessage("success", "Subscription cancelled.")
                setIsCancelModalVisible(false)
            },
            onCancel: () => setIsCancelModalVisible(false),
        })
    }

    const handleBillingContactUpdate = (values) => {
        showMessage("success", "Billing contact updated.")
    }

    return (
        <Card className="mb-6">
            <div className="mb-4">
                <Title level={4}>Billing & Subscription</Title>
                <Text type="secondary">Manage your plan, invoices, and payment methods.</Text>
            </div>

            <Title level={5} className="mb-4">
                Current Plan Overview
            </Title>
            <Card className="mb-6">
                <div className="flex justify-between items-start">
                    <div>
                        <Text strong className="text-lg">
                            Pro Plan
                        </Text>
                        <Text className="block text-2xl font-bold mt-1">$199/month</Text>
                        <ul className="list-disc list-inside text-sm text-gray-600 mt-2">
                            <li>✓ Real-time signals</li>
                            <li>✓ AI forecasting</li>
                            <li>✓ 5 integrations</li>
                        </ul>
                    </div>
                    <div className="flex flex-col items-end">
                        <Tag color="green" className="mb-2">
                            <CheckCircleOutlined /> Active
                        </Tag>
                        <Button type="primary">Upgrade Plan</Button>
                    </div>
                </div>
            </Card>

            <Title level={5} className="mb-4">
                Billing History
            </Title>
            <Table
                dataSource={billingHistoryData}
                columns={billingHistoryColumns}
                pagination={{ pageSize: 5 }}
                className="mb-6"
            />

            <Title level={5} className="mb-4">
                Payment Method
            </Title>
            <Card className="mb-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        <CreditCardOutlined className="text-2xl mr-3" />
                        <div>
                            <Text strong>Visa ending in 4242</Text>
                            <Text type="secondary" className="block text-sm">
                                Exp. 10/26
                            </Text>
                        </div>
                    </div>
                    <Button onClick={() => showMessage("info", "Opening payment method update form")}>
                        Update Payment Method
                    </Button>
                </div>
            </Card>

            <Title level={5} className="mb-4">
                Cancel or Pause Subscription
            </Title>
            <Button danger onClick={() => setIsCancelModalVisible(true)}>
                Cancel Subscription
            </Button>
            <Modal
                title="Cancel Subscription"
                visible={isCancelModalVisible}
                onOk={handleCancelSubscription}
                onCancel={() => setIsCancelModalVisible(false)}
                okText="Confirm Cancellation"
                cancelText="Keep Subscription"
            >
                <p>Are you sure you want to cancel your subscription?</p>
                <p className="text-red-500 mt-2">You will retain access until the end of your current billing cycle.</p>
            </Modal>

            <Title level={5} className="mt-6 mb-4">
                Billing Contact
            </Title>
            <Form form={billingContactForm} onFinish={handleBillingContactUpdate} layout="vertical">
                <Form.Item name="companyName" label="Company Name" initialValue="Acme Corp">
                    <Input />
                </Form.Item>
                <Form.Item
                    name="billingEmail"
                    label="Billing Email"
                    initialValue="billing@acmecorp.com"
                    rules={[{ type: "email" }]}
                >
                    <Input />
                </Form.Item>
                <Form.Item name="billingAddress" label="Billing Address" initialValue="123 Main St, Anytown, USA">
                    <Input.TextArea rows={2} />
                </Form.Item>
                <Button type="primary" htmlType="submit">
                    Update Billing Contact
                </Button>
            </Form>
        </Card>
    )
}

export default BillingSubscription;